import { nbxVar } from '../../tokens/generated/nbx-tokens.js';
import { createTableTokens } from '../tokens/components.js';
import { SEMANTIC, type SemanticTokens } from '../tokens/semantic.js';

/**
 * DataTable Component Configuration — data table with sorting, filtering, and selection.
 * Header: ink background, white 14/20 semibold text, 53px height. Rows: white background, 76px height.
 * Text: dark blue (#08183a), Poppins Regular 14px. Borders: light gray divider (#e7e8ec).
 */
export function createDatatable(s: SemanticTokens) {
  const tableTokens = createTableTokens(s);
  return {
    root: { borderColor: 'transparent', transitionDuration: tableTokens.transitionDuration },
    header: {
      background: tableTokens.headerBg,
      borderColor: 'transparent',
      borderWidth: '0',
      color: tableTokens.headerFg,
      padding: tableTokens.padding,
    },
    headerCell: {
      background: tableTokens.headerBg,
      hoverBackground: tableTokens.headerBg,
      selectedBackground: tableTokens.headerBg,
      borderColor: 'transparent',
      color: tableTokens.headerFg,
      hoverColor: tableTokens.headerFg,
      selectedColor: tableTokens.headerFg,
      padding: tableTokens.headerPadding,
      focusRing: { width: '0', style: 'none', color: 'transparent', offset: '0', shadow: 'none' },
    },
    columnTitle: { fontWeight: tableTokens.columnTitleFontWeight },
    row: {
      background: tableTokens.rowBg,
      hoverBackground: tableTokens.rowBgHover,
      selectedBackground: tableTokens.rowBgSelected,
      color: tableTokens.rowFg,
      hoverColor: tableTokens.rowFgHover,
      selectedColor: tableTokens.rowFgSelected,
      focusRing: { width: '0', style: 'none', color: 'transparent', offset: '0', shadow: 'none' },
      stripedBackground: tableTokens.stripedBg,
    },
    bodyCell: {
      borderColor: tableTokens.tableBorder,
      padding: tableTokens.padding,
      selectedBorderColor: 'transparent',
    },
    footerCell: {
      background: tableTokens.footerBg,
      borderColor: tableTokens.footerBorder,
      color: tableTokens.footerFg,
      padding: tableTokens.padding,
    },
    columnFooter: { fontWeight: tableTokens.footerFontWeight },
    footer: {
      background: tableTokens.footerBg,
      borderColor: tableTokens.footerBorder,
      borderWidth: '0 0 1px 0',
      color: tableTokens.footerFg,
      padding: tableTokens.padding,
    },
    dropPoint: { color: tableTokens.dropPointFg },
    resizeIndicator: { width: '1px', color: tableTokens.resizeIndicatorFg },
    sortIcon: {
      color: tableTokens.sortIconFg,
      hoverColor: tableTokens.sortIconFg,
      size: tableTokens.sortIconSize,
    },
    loadingIcon: { size: tableTokens.loadingIconSize },
    rowToggleButton: {
      hoverBackground: tableTokens.rowToggle.bgHover,
      selectedHoverBackground: tableTokens.rowToggle.bgSelectedHover,
      color: tableTokens.rowToggle.fg,
      hoverColor: tableTokens.rowToggle.fgHover,
      selectedHoverColor: tableTokens.rowToggle.fgSelectedHover,
      size: tableTokens.rowToggleSize,
      borderRadius: tableTokens.rowToggleRadius,
      focusRing: { width: '0', style: 'none', color: 'transparent', offset: '0', shadow: 'none' },
    },
    paginatorTop: { borderColor: tableTokens.tableBorder, borderWidth: '0 0 1px 0' },
    paginatorBottom: { borderColor: tableTokens.tableBorder, borderWidth: '0 0 1px 0' },
    colorScheme: {
      light: {
        header: {
          background: tableTokens.headerBg,
          borderColor: 'transparent',
          color: tableTokens.headerFg,
        },
        headerCell: {
          background: tableTokens.headerBg,
          hoverBackground: tableTokens.headerBg,
          selectedBackground: tableTokens.headerBg,
          borderColor: 'transparent',
          color: tableTokens.headerFg,
          hoverColor: tableTokens.headerFg,
          selectedColor: tableTokens.headerFg,
        },
        row: {
          background: tableTokens.rowBg,
          hoverBackground: tableTokens.rowBgHover,
          selectedBackground: tableTokens.rowBgSelected,
          color: tableTokens.rowFg,
          hoverColor: tableTokens.rowFgHover,
          selectedColor: tableTokens.rowFgSelected,
        },
        bodyCell: { borderColor: tableTokens.tableBorder },
        footer: {
          background: tableTokens.footerBg,
          borderColor: 'transparent',
          color: tableTokens.footerFg,
        },
      },
    },
    css: () => `
    /* Base table styles */
    .p-datatable {
      overflow: auto;
      border-radius: ${tableTokens.radius};
      font-family: ${tableTokens.fontFamily};
    }

    /* Header styles */
    .p-datatable .p-datatable-thead > tr > th {
      height: ${nbxVar('table.header-height')};
      font-size: ${tableTokens.headerFontSize};
      font-weight: ${tableTokens.headerFontWeight};
      line-height: ${tableTokens.headerLineHeight};
    }

    /* Disable header hover effect (solid color) */
    .p-datatable .p-datatable-thead > tr > th:hover {
      background: ${tableTokens.headerBg};
    }

    /* Body cell text styles */
    .p-datatable .p-datatable-tbody > tr > td {
      font-size: ${tableTokens.bodyFontSize};
      font-weight: ${tableTokens.bodyFontWeight};
      line-height: ${tableTokens.bodyLineHeight};
    }

    .p-datatable .p-datatable-tbody > tr {
      height: ${nbxVar('table.row-height')};
    }

    /* Semibold text for specific cells (e.g., rental names) */
    .p-datatable .p-datatable-tbody > tr > td.font-semibold,
    .p-datatable .p-datatable-tbody > tr > td [class*="semibold"] {
      font-weight: ${tableTokens.bodySemiboldWeight};
    }

    /* Ensure header respects card border radius */
    .p-datatable .p-datatable-thead > tr:first-child > th:first-child {
      border-top-left-radius: ${tableTokens.radius};
    }

    .p-datatable .p-datatable-thead > tr:first-child > th:last-child {
      border-top-right-radius: ${tableTokens.radius};
    }

    /* Ensure last row respects card border radius */
    .p-datatable .p-datatable-tbody > tr:last-child > td:first-child {
      border-bottom-left-radius: ${tableTokens.radius};
    }

    .p-datatable .p-datatable-tbody > tr:last-child > td:last-child {
      border-bottom-right-radius: ${tableTokens.radius};
    }

    /* If there's a footer, apply radius to footer cells instead */
    .p-datatable .p-datatable-tfoot > tr:last-child > td:first-child {
      border-bottom-left-radius: ${tableTokens.radius};
    }

    .p-datatable .p-datatable-tfoot > tr:last-child > td:last-child {
      border-bottom-right-radius: ${tableTokens.radius};
    }
  `,
  };
}
export const datatable = createDatatable(SEMANTIC);
